import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TableData } from "../../types/table-filter-types";
import { getDictionaries, getKrookies, getKrookieStatuses } from "../api";
import { formatTableTime } from "../../utils/format-table-dates";
import { LayersDataTypes } from "../../types/layers-data-types";
import type { RootState } from "../store";

interface InitialState {
  fullData: TableData;
  data: TableData;
  totalDataSize: number;
  selected: Array<number>;
  landuseDictionary: Array<{ code: number; name: string }>;
  statusesDictionary: Array<{ id: number; name: string }>;
  citiesDictionary: Array<{ code: number; name: string }>;
  isLoading: boolean;
}

const initialState: InitialState = {
  fullData: [],
  data: [],
  totalDataSize: 0,
  selected: [],
  landuseDictionary: [],
  statusesDictionary: [],
  citiesDictionary: [],
  isLoading: false,
};

export const fetchKrookies = createAsyncThunk(
  "table/fetchKrookies",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { layers, arcgisTokens } = state.map;
    const { citiesDictionary, landuseDictionary, statusesDictionary } =
      state.table;
    const { user } = state.user;

    const dictsLoaded =
      citiesDictionary.length > 0 && landuseDictionary.length > 0;

    const plotLayer = layers.find(
      (layer) => layer.dataType === LayersDataTypes.Plots && layer.url,
    );

    const resPromise = getKrookies();

    let cities = citiesDictionary;
    let landUse = landuseDictionary;
    let statuses = statusesDictionary;

    if (!plotLayer) return rejectWithValue("Plot layer not found");

    if (!arcgisTokens || Object.keys(arcgisTokens).length === 0)
      return rejectWithValue("ArcGIS token is missing");

    if (!dictsLoaded) {
      const [fetchedStatuses, fetchedDicts] = await Promise.all([
        getKrookieStatuses(),
        getDictionaries(plotLayer, arcgisTokens),
      ]);

      if (!fetchedStatuses)
        return rejectWithValue("Failed to load krookie statuses");

      if (!fetchedDicts.cities.length || !fetchedDicts.landUse.length)
        return rejectWithValue("Failed to load dictionaries");

      statuses = fetchedStatuses;
      cities = fetchedDicts.cities;
      landUse = fetchedDicts.landUse;
    }

    const res = await resPromise;
    if (!res) return rejectWithValue("Failed to load krookies data");

    let filtered = res;
    if (user && Array.isArray(user.cities) && user.cities.length > 0) {
      filtered = res.filter((item: any) => user.cities?.includes(item.cityId));
    }

    const mapped = filtered.map((item: any) => {
      let res2: Record<string, any> = {};

      for (const key in item) {
        if (key !== "cityId") {
          res2[key] = item[key];
        }
      }

      if (cities.length > 0) {
        const city = cities.find((obj: any) => obj.code == item.cityId);
        res2.city = city?.name ?? "";
        res2.cityAndAddress = `${res2.city}\n${item.streetNum}`;
      }

      if (landUse.length > 0) {
        const lu = landUse.find((obj: any) => obj.code == item.landUse);
        res2.landUse = lu?.name ?? "";
      }

      res2.timeAndDrafter = `${formatTableTime(new Date(item.changedDateTime), "MM/DD/YYYY hh:mm pm")}\n${item.userName}`;

      return { ...res2 };
    });

    mapped.sort((a: any, b: any) => b.id - a.id);

    return { fullData: mapped, cities, landUse, statuses };
  },
);

export const tableSlice = createSlice({
  name: "table",
  initialState: initialState,
  reducers: {
    setData: (state, { payload }) => {
      state.data = payload.data;
      state.totalDataSize = payload.totalDataSize;
    },
    addSelected: (state, { payload }) => {
      state.selected.push(...payload);
    },
    deleteSelected: (state, { payload }) => {
      let newSelected = state.selected;
      payload.forEach((item1: any) => {
        newSelected = newSelected.filter((item2) => item1 != item2);
      });
      state.selected = newSelected;
    },
    deleteAllSelected: (state) => {
      state.selected = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKrookies.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchKrookies.fulfilled, (state, action) => {
        state.fullData = action.payload.fullData;
        if (action.payload.cities.length > 0) {
          state.citiesDictionary = action.payload.cities;
          state.landuseDictionary = action.payload.landUse;
          state.statusesDictionary = action.payload.statuses;
        }
        state.isLoading = false;
      })
      .addCase(fetchKrookies.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setData, addSelected, deleteAllSelected, deleteSelected } =
  tableSlice.actions;
